import { useEffect, useRef } from 'react';
import { RearrangeIcon } from '../../../bombiix-icon';
import type { BombiixCellRendererParams, BombiixRow } from '../types';

/**
 * Deliberate exception to the size={24} icon convention: the handle sits in a 36px structural
 * column and must not grow the row, matching the 20px expand chevron beside it.
 */
const DRAG_HANDLE_SIZE = 20;

interface RowDragExtras {
  /** Rows this returns false for render no handle and cannot be dragged. */
  canRowDrag?: (row: BombiixRow | undefined) => boolean;
  dragTitle?: string;
}

type RowDragCellProps = BombiixCellRendererParams & RowDragExtras;

/**
 * Drag handle for a reorderable row.
 *
 * AG Grid's `rowDragManaged` normally makes the whole row the drag source. `registerRowDragger`
 * narrows that to this handle, so a row stays clickable and its inputs stay usable — which is what
 * every hand-rolled copy of this cell in the app was doing before the grid grew the capability.
 *
 * Returning `null` for a row is what makes a row undraggable: with no registered dragger AG Grid has
 * nothing to start a drag from. The cell still occupies its column so the rows stay aligned.
 */
function RowDragCell(params: RowDragCellProps) {
  const { data, canRowDrag, dragTitle = 'Drag to reorder', registerRowDragger } = params;
  const handleRef = useRef<HTMLSpanElement>(null);
  const draggable = typeof canRowDrag === 'function' ? canRowDrag(data) : true;

  useEffect(() => {
    if (handleRef.current && draggable && typeof registerRowDragger === 'function') {
      registerRowDragger(handleRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!draggable) return null;

  return (
    <span ref={handleRef} className="ag-grid-drag-handle" title={dragTitle}>
      <span className="bombiix-icon-container bombiix-icon-container--blue">
        <RearrangeIcon size={DRAG_HANDLE_SIZE} />
      </span>
    </span>
  );
}

export default RowDragCell;
